import React, { useState, useEffect } from 'react';
import { crawlingService } from '../../services/crawlingService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';

const CrawlingStats = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [domainStats, setDomainStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadStats();
    if (isAdmin()) {
      loadDomainStats();
    }
  }, []);

  const loadStats = async () => {
    setLoading(true);
    const { data, error } = await crawlingService.getCrawlStats();
    
    if (data) {
      setStats(data);
    } else if (error) {
      toast.error('Failed to load crawling statistics');
      console.error('Error loading stats:', error);
    }
    
    setLoading(false);
  };

  const loadDomainStats = async () => {
    const { data, error } = await crawlingService.getDomainStats();
    
    if (data) {
      setDomainStats(data);
    } else if (error) {
      console.error('Error loading domain stats:', error);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <span className="text-2xl">{icon}</span>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  const ProgressBar = ({ label, value, total, color = 'blue' }) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    
    return (
      <div className="mb-4">
        <div className="flex justify-between text-sm font-medium text-gray-700 mb-1">
          <span>{label}</span>
          <span>{value} / {total}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`bg-${color}-500 h-2 rounded-full transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {percentage.toFixed(1)}%
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-gray-900">Crawling Statistics</h1>
            <button
              onClick={loadStats}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('links')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'links'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Link Analysis
            </button>
            {isAdmin() && (
              <button
                onClick={() => setActiveTab('domains')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'domains'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Domain Stats
              </button>
            )}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Main Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Crawl Tasks"
                value={stats.total_crawl_tasks?.toLocaleString() || '0'}
                subtitle={`${stats.recent_activity_30_days || 0} in last 30 days`}
                icon="📋"
                color="blue"
              />
              <StatCard
                title="Success Rate"
                value={`${stats.success_rate || 0}%`}
                subtitle={`${stats.completed_tasks || 0} completed`}
                icon="✅"
                color="green"
              />
              <StatCard
                title="Social Links Found"
                value={stats.total_social_links?.toLocaleString() || '0'}
                subtitle={`${stats.verified_links || 0} verified`}
                icon="🔗"
                color="purple"
              />
              <StatCard
                title="Average Duration"
                value={stats.average_crawl_duration ? `${stats.average_crawl_duration}s` : 'N/A'}
                subtitle="Per crawl task"
                icon="⏱️"
                color="orange"
              />
            </div>

            {/* Task Status Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ProgressBar
                    label="Completed Tasks"
                    value={stats.completed_tasks || 0}
                    total={stats.total_crawl_tasks || 0}
                    color="green"
                  />
                  <ProgressBar
                    label="Failed Tasks"
                    value={stats.failed_tasks || 0}
                    total={stats.total_crawl_tasks || 0}
                    color="red"
                  />
                  <ProgressBar
                    label="Pending Tasks"
                    value={stats.pending_tasks || 0}
                    total={stats.total_crawl_tasks || 0}
                    color="yellow"
                  />
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      {stats.success_rate || 0}%
                    </div>
                    <div className="text-sm text-gray-500">
                      Overall Success Rate
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity (30 Days)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">
                    {stats.recent_activity_30_days || 0}
                  </div>
                  <div className="text-sm text-gray-500">Crawl Tasks</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-2">
                    {stats.total_social_links || 0}
                  </div>
                  <div className="text-sm text-gray-500">Social Links Found</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600 mb-2">
                    {stats.verified_links || 0}
                  </div>
                  <div className="text-sm text-gray-500">Links Verified</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Link Analysis Tab */}
        {activeTab === 'links' && stats && (
          <div className="space-y-6">
            {/* Link Type Distribution */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Link Type Distribution</h3>
              {stats.link_types && stats.link_types.length > 0 ? (
                <div className="space-y-4">
                  {stats.link_types.map((linkType, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">
                          {crawlingService.getPlatformIcon(linkType.link_type)}
                        </span>
                        <span className="font-medium text-gray-900 capitalize">
                          {linkType.link_type.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="text-right mr-4">
                          <div className="font-semibold text-gray-900">
                            {linkType.count.toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {((linkType.count / stats.total_social_links) * 100).toFixed(1)}%
                          </div>
                        </div>
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${(linkType.count / stats.total_social_links) * 100}%`,
                              backgroundColor: crawlingService.getPlatformColor(linkType.link_type)
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No link type data available
                </div>
              )}
            </div>
          </div>
        )}

        {/* Domain Stats Tab (Admin Only) */}
        {activeTab === 'domains' && isAdmin() && domainStats && (
          <div className="space-y-6">
            {/* Top Domains */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Crawled Domains</h3>
              {domainStats.top_domains && domainStats.top_domains.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Domain
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Requests
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Success Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Request
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {domainStats.top_domains.map((domain, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {domain.domain}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {domain.total_requests.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              domain.success_rate >= 80 
                                ? 'bg-green-100 text-green-800'
                                : domain.success_rate >= 50
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {domain.success_rate}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              domain.is_rate_limited
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {domain.is_rate_limited ? 'Rate Limited' : 'Normal'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {domain.last_request_at 
                              ? crawlingService.formatTimestamp(domain.last_request_at)
                              : 'Never'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No domain data available
                </div>
              )}
            </div>

            {/* Rate Limited Domains */}
            {domainStats.rate_limited_domains && domainStats.rate_limited_domains.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Rate Limited Domains</h3>
                <div className="space-y-3">
                  {domainStats.rate_limited_domains.map((domain, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{domain.domain}</div>
                        <div className="text-sm text-gray-500">
                          {domain.total_requests} total requests
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-red-600">
                          Rate limited until
                        </div>
                        <div className="text-sm text-gray-500">
                          {crawlingService.formatTimestamp(domain.rate_limit_until)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Problematic Domains */}
            {domainStats.problematic_domains && domainStats.problematic_domains.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Problematic Domains (Low Success Rate)</h3>
                <div className="space-y-3">
                  {domainStats.problematic_domains.map((domain, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{domain.domain}</div>
                        <div className="text-sm text-gray-500">
                          {domain.successful_requests} / {domain.total_requests} successful
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                          {domain.success_rate}% success
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Data State */}
        {!stats && !loading && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Statistics Available</h3>
            <p className="text-gray-500">Statistics will appear here after crawling tasks are completed.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrawlingStats;